import React from 'react';
import { useTranslation } from 'react-i18next';
import Config from 'config';
import { Box, Button, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import {
  AddCircle as AddCircleIcon,
  InsertDriveFile as InsertDriveFileIcon
} from '@material-ui/icons';
import useUserStore from 'store/user';
import { getRosteringType } from '../index';
import ResourcesBox from './ResourcesBox';
import RosteringExpansionPanel, { Summary } from './RosteringExpansionPanel';
import { RosteringType } from './RosteringTypeSelectInput';
import SteppedInstructions, { Step } from './SteppedInstructions';

type SectionProps = {
  title: string;
  children?: React.ReactNode;
};

const ManualEntry = () => {
  const classes = useStyles();
  const { t } = useTranslation('AdminRosterManagement');
  const [{ settings }] = useUserStore();

  const type = getRosteringType(settings) ?? '';

  return (
    <RosteringExpansionPanel
      id="manual-entry-panel"
      square={false}
      summary={
        <Summary
          title={t('manualEntryTitle')}
          subtitle={
            <>
              {t('manualEntrySubtitle')}
              {type === RosteringType.DISTRICT && (
                <span className={classes.warningNote}> ({t('manualEntrySubtitleNote')})</span>
              )}
            </>
          }
        />
      }
      icon={<InsertDriveFileIcon className={classes.icon} />}
    >
      <Details />
    </RosteringExpansionPanel>
  );
};

const Details = () => {
  const classes = useStyles();
  const { t } = useTranslation('AdminRosterManagement');

  const usersVideo = {
    src: `${Config.s3Url}/support/video/PLI-Creating_Licensing_Educators.mp4`,
    title: t('Assign Licenses to Users Video')
  };

  const studentsVideo = {
    src: `${Config.s3Url}/support/video/PLI-Creating_Licensing_Students.mp4`,
    title: t('Assign Licenses to Students Video')
  };

  return (
    <div>
      <RosteringExpansionPanel
        id="create-license-educators"
        aria-controls="create-license-educators"
        square
        summary={<Typography variant="h4">{t('Creating and Licensing Educators')}</Typography>}
      >
        <ResourcesBox video={usersVideo} className={classes.resources} />
        <Section title={t('To create and assign licenses to NEW educators')}>
          <NewEducatorInstructions />
        </Section>
        <Section
          title={t('To assign licenses to educators ALREADY LISTED in your User Management tile')}
        >
          <ExistingEducatorInstructions />
        </Section>
      </RosteringExpansionPanel>
      <RosteringExpansionPanel
        id="create-license-students"
        aria-controls="create-license-students"
        square
        summary={<Typography variant="h4">{t('Creating and Licensing Students')}</Typography>}
      >
        <ResourcesBox video={studentsVideo} className={classes.resources} />
        <Section title={t('To Create Classes and License Students when YOU are the teacher')}>
          <TeacherStudentInstructions />
        </Section>
        <Section title={t('To Create Classes and License Students when YOU are the Admin')}>
          <AdminStudentInstructions />
        </Section>
      </RosteringExpansionPanel>
    </div>
  );
};

const Section = ({ title, children }: SectionProps) => {
  const classes = useStyles();

  return (
    <Box className={classes.section}>
      <Typography variant="h4" className={classes.sectionTitle}>
        {title}
      </Typography>
      <Box paddingX={2}>{children}</Box>
    </Box>
  );
};

const NewEducatorInstructions = () => {
  const { t } = useTranslation('AdminRosterManagement');

  return (
    <SteppedInstructions>
      <Step number={1}>
        <Typography variant="subtitle1">
          {t('From the Admin Console, select the User Management tile and click on the ')}
          <Button
            aria-label="Invite User"
            variant="contained"
            color="primary"
            size="large"
            disableElevation
            startIcon={<AddCircleIcon />}
          >
            {t('Invite User')}
          </Button>
          {t(' button in the top right navigation.')}
        </Typography>
      </Step>
      <Step number={2}>
        <Typography variant="subtitle1">{t('Fill in the appropriate information:')}</Typography>
        <Typography variant="subtitle1">
          <ol>
            <li>{t('Input name and email address of educator.')}</li>
            <li>{t('Choose organization and role and select “Add Role”.')}</li>
            <li>
              {t('Assign product(s) by using the drop down menu and select “Assign Product.”')}
            </li>
          </ol>
        </Typography>
      </Step>
      <Step number={3}>
        <Typography variant="subtitle1">{t('Click on Invite.')}</Typography>
      </Step>
    </SteppedInstructions>
  );
};

const ExistingEducatorInstructions = () => {
  const classes = useStyles();
  const { t } = useTranslation('AdminRosterManagement');

  return (
    <SteppedInstructions>
      <Step number={1}>
        <Typography variant="subtitle1">
          {t(
            'From the Admin Console, select the User Management tile and locate the educator you would like to assign a license. Click on the 3 dots in the top right of their named tile and select Manage User Products.'
          )}
        </Typography>
      </Step>
      <Step number={2}>
        <Typography variant="subtitle1">
          {t('Find the license you would like to assign and select the ')}
          <Button
            aria-label="Assign"
            variant="contained"
            color="secondary"
            size="small"
            className={classes.assignButton}
            disableElevation
          >
            {t('Assign')}
          </Button>
          {t(' button.')}
        </Typography>
      </Step>
      <Step number={3}>
        <Typography variant="subtitle1">{t('Click on Save & Close.')}</Typography>
      </Step>
    </SteppedInstructions>
  );
};

const TeacherStudentInstructions = () => {
  const { t } = useTranslation('AdminRosterManagement');

  return (
    <SteppedInstructions>
      <Step number={1}>
        <Typography variant="subtitle1">
          {t('Go to My Classes & Students in the left-hand navigation.')}
        </Typography>
      </Step>
      <Step number={2}>
        <Typography variant="subtitle1">
          {t('Click on the ')}
          <Button
            aria-label="Add Button"
            variant="contained"
            color="primary"
            size="large"
            disableElevation
            startIcon={<AddCircleIcon />}
          >
            {t('Create Class')}
          </Button>
          {t(' button in the top right-hand corner.')}
        </Typography>
      </Step>
      <Step number={3}>
        <Typography variant="subtitle1">
          {t(
            'Go through steps 1-4 in the "Create Class" window, filling in the necessary Class Details:'
          )}
        </Typography>
        <Typography variant="subtitle1">
          <ol>
            <li>{t('Create Class')}</li>
            <li>{t('Add Students')}</li>
            <li>{t('Assign Products')}</li>
            <li>{t('Review & Confirm')}</li>
          </ol>
        </Typography>
      </Step>
      <Step number={4}>
        <Typography variant="subtitle1">{t('Click on Confirm & Create Class.')}</Typography>
      </Step>
    </SteppedInstructions>
  );
};

const AdminStudentInstructions = () => {
  const { t } = useTranslation('AdminRosterManagement');

  return (
    <SteppedInstructions>
      <Step number={1}>
        <Typography variant="subtitle1">
          <strong>{t('NOTE: ')}</strong>
          {t('If educators are not already existing in the system, complete that step first.')}
        </Typography>
        <Typography variant="subtitle1">
          {t('Go to the Admin Console and select the Classes & Students tile.')}
        </Typography>
      </Step>
      <Step number={2}>
        <Typography variant="subtitle1">
          {t('Click on the ')}
          <Button
            aria-label="Add Button"
            variant="contained"
            color="primary"
            size="large"
            disableElevation
            startIcon={<AddCircleIcon />}
          >
            {t('Create Class')}
          </Button>
          {t(' button in the top right-hand corner.')}
        </Typography>
      </Step>
      <Step number={3}>
        <Typography variant="subtitle1">
          {t(
            'Go through steps 1-4 in the "Create Class" window, filling in the necessary Class Details:'
          )}
        </Typography>
        <Typography variant="subtitle1">
          <ol>
            <li>{t('Create Class')}</li>
            <li>{t('Add Students')}</li>
            <li>{t('Assign Products')}</li>
            <li>{t('Review & Confirm')}</li>
          </ol>
        </Typography>
      </Step>
      <Step number={4}>
        <Typography variant="subtitle1">{t('Click on Confirm & Create Class.')}</Typography>
      </Step>
    </SteppedInstructions>
  );
};

const useStyles = makeStyles(theme => ({
  icon: {
    height: 46,
    width: 46,
    margin: 'auto',
    color: theme.palette.secondary.main
  },
  warningNote: {
    color: theme.palette.warning.main,
    fontWeight: 500
  },
  assignButton: {
    background: '#FFFFFF',
    border: '1px solid #969696',
    color: '#2D2D2D',
    '&:hover': {
      background: '#FFFFFF'
    }
  },
  resources: {
    margin: theme.spacing(2, 0)
  },
  section: {
    margin: theme.spacing(2, 0)
  },
  sectionTitle: {
    textDecoration: 'underline',
    marginBottom: theme.spacing(1)
  }
}));

export default ManualEntry;
